import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { emailCampaignsApi, type CampaignDashboard } from '../api';
import { Loader2, ArrowLeft } from 'lucide-react';

const POLL_INTERVAL_MS = 5000;

export default function EmailCampaignDashboardPage() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CampaignDashboard | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await emailCampaignsApi.dashboard(Number(id));
        if (!cancelled) setData(res);
      } catch (error: any) {
        if (!cancelled) {
          toast({ variant: 'destructive', title: 'Error', description: 'No se pudo cargar el dashboard.' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id]);

  if (loading || !data) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </Layout>
    );
  }

  const { campaign, bySource } = data;
  const processed = campaign.acceptedCount + campaign.deliveredCount + campaign.failedCount + campaign.bounceCount + campaign.skippedCount;

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6 max-w-3xl">
        <Link to={`/admin/email-campaigns/${id}`}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" /> Volver a la campaña
          </Button>
        </Link>

        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{campaign.name}</h1>
          <Badge>{campaign.status}</Badge>
        </div>

        <Card className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Stat label="Total" value={campaign.totalRecipients} />
          <Stat label="Pendientes" value={campaign.pendingCount} />
          <Stat label="Aceptados por SES" value={campaign.acceptedCount} />
          <Stat label="Entregados" value={campaign.deliveredCount} />
          <Stat label="Bounces" value={campaign.bounceCount} />
          <Stat label="Complaints" value={campaign.complaintCount} />
          <Stat label="Fallas" value={campaign.failedCount} />
          <Stat label="Omitidos" value={campaign.skippedCount} />
        </Card>

        <Card className="p-6">
          <p className="text-sm text-muted-foreground mb-2">
            Procesados {processed} de {campaign.totalRecipients}
          </p>
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${campaign.totalRecipients ? (processed / campaign.totalRecipients) * 100 : 0}%` }}
            />
          </div>
        </Card>

        <Card className="p-6 space-y-3">
          <h2 className="text-lg font-semibold">Resultado por origen</h2>
          {Object.keys(bySource).length === 0 ? (
            <p className="text-muted-foreground text-sm">Sin datos aún.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {Object.entries(bySource).map(([source, stats]) => (
                <div key={source} className="flex items-center justify-between border-b pb-2">
                  <span>{source}</span>
                  <span className="text-muted-foreground">
                    {stats.total} total · {stats.delivered} entregados · {stats.bounced} bounces
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {campaign.lastError && (
          <Card className="p-6 bg-destructive/10 border-destructive">
            <p className="text-sm text-destructive">{campaign.lastError}</p>
          </Card>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold">{value}</p>
    </div>
  );
}
